/*
# -----------------------------------------------------------------------------
#	Project: HSE Genius®
#	Client ID: 834215
#	Registration Code: IT-2025-ZR319-MQ744
#	Author: KaimakiWeb
#	Website: https://kaimakiweb.com
#	Copyright (c) 2024-2026 Kaimaki Web. All rights reserved.
#
#	This software and its source code are the property of KaimakiWeb.
#	Unauthorized copying, modification, distribution, or use of this software,
#	via any medium, is strictly prohibited unless prior written permission
#	is obtained from KaimakiWeb.
# -----------------------------------------------------------------------------
*/

import axios from "axios";
import { showToast } from "src/components/ui/toast/service";
import i18n from "src/i18n";

// Maps a snippet of a raw DRF/SimpleJWT error onto a translation key, so the
// toast follows the language selected in the UI rather than being hardcoded.
// Patterns are matched against the English source strings — the backend also
// honours Accept-Language, so anything that misses here still falls back to the
// (already localised) server message rather than to the generic text.
const BACKEND_ERROR_MAP = [
  ["unable to log in with provided credentials", "api-error.invalid-credentials"],
  ["no active account found", "api-error.invalid-credentials"],
  ["authentication credentials were not provided", "api-error.session-expired"],
  ["already been taken", "api-error.already-taken"],
  ["already exists", "api-error.already-exists"],
  ["this field is required", "api-error.field-required"],
  ["this field may not be blank", "api-error.field-blank"],
  ["this field may not be null", "api-error.field-blank"],
  ["enter a valid email address", "api-error.invalid-email"],
  ["a valid integer is required", "api-error.invalid-integer"],
  ["ensure this value has at most", "api-error.value-too-long"],
  ["ensure this value has at least", "api-error.value-too-short"],
  ["invalid pk", "api-error.invalid-selection"],
  ["invalid literal for int", "api-error.invalid-number"],
  ["permission denied", "api-error.permission-denied"],
  ["you do not have permission", "api-error.permission-denied"],
  ["not found", "api-error.not-found"],
  ["password is too short", "api-error.password-too-short"],
  ["password is too common", "api-error.password-too-common"],
  ["password is entirely numeric", "api-error.password-numeric"],
  ["the two password fields", "api-error.password-mismatch"],
  ["request was throttled", "api-error.throttled"],
  ["credit_limit", "api-error.credit-limit"],
  ["request_limit", "api-error.request-limit"],
  ["file size", "api-error.file-size"],
  ["invalid file type", "api-error.file-type"],
];

const genericError = () => i18n.t("api-error.generic");

const translateSnippet = (text) => {
  if (!text || typeof text !== "string") return null;
  const lower = text.toLowerCase();
  for (const [pattern, key] of BACKEND_ERROR_MAP) {
    if (lower.includes(pattern)) return i18n.t(key);
  }
  return null;
};

const humanizeError = (raw) => {
  if (!raw || typeof raw !== "string") return genericError();

  if (raw.includes("\n")) {
    const translated = raw
      .split("\n")
      .map((line) => {
        const colonIdx = line.indexOf(": ");
        if (colonIdx !== -1) {
          const field = line.slice(0, colonIdx);
          const msg = line.slice(colonIdx + 2);
          return `${field}: ${translateSnippet(msg) ?? msg}`;
        }
        return translateSnippet(line) ?? line;
      })
      .filter(Boolean);
    return translated.length > 0 ? translated.join("\n") : genericError();
  }

  // Unmapped messages are shown as-is: the backend localises them via
  // Accept-Language, so they are more useful than the generic fallback.
  return translateSnippet(raw) ?? raw;
};

export const setupToastErrors = (error) => {
  // Intentional cancellations (e.g. invite flow blocking /files/counter) are
  // not failures — never show a user-facing toast for them.
  if (axios.isCancel(error)) return;

  const config = error?.config;
  const method = config?.method?.toLowerCase();
  const data = error?.response?.data;

  // Token refresh is internal session machinery. When it fails there is simply
  // no session to recover (e.g. right after logout, when the refresh cookie is
  // gone) and the caller already redirects to /login — surfacing DRF's
  // "This field is required." for the missing refresh token only confuses.
  if ((config?.url || "").includes("/user/token/refresh/")) return;

  if (method === "get" || data?.code === "token_not_valid") return;

  // Skip toast for "EER already present" so it appears only in the confirmation dialog after user clicks Validate (not on page load / other requests)
  const errorMessageForSkip = data?.detail || data?.message || data?.error || "";
  if (
    errorMessageForSkip.includes("already present") ||
    errorMessageForSkip.includes("existing EER") ||
    errorMessageForSkip.includes("HP Finder")
  ) {
    return;
  }

  // Ignore "No UserDocumentDetail matches" errors - item already deleted
  const errorMessage = data?.detail || data?.message || data?.error || "";
  if (errorMessage.includes("No UserDocumentDetail matches") && 
      (method === "delete" || method === "patch")) {
    return;
  }

  const formatFieldName = (key) =>
    key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  const extractBackendMessage = () => {
    // First: Try common keys
    let message = data?.message || data?.detail || data?.error;

    // if (method === "get")
    //   message = t("config.fetch.error", {
    //     defaultValue: "An unexpected error occurred",
    //   });

    // Next: Try to build field-level messages if applicable
    if (
      typeof message !== "string" &&
      typeof data === "object" &&
      !Array.isArray(data)
    ) {
      const fieldErrors = Object.entries(data)
        .filter(([_, value]) => Array.isArray(value))
        .map(
          ([field, messages]) =>
            `${formatFieldName(field)}: ${messages.join(", ")}`
        )
        .join("\n");

      if (fieldErrors) {
        message = fieldErrors;
      }
    }

    return message;
  };

  const backendMessage = extractBackendMessage();
  showToast(humanizeError(backendMessage), "error");

  // Re-throw so callers receive an AxiosError (not undefined) and can decide
  // whether to add context or simply let this toast be the only notification.
  return Promise.reject(error);
};
